import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import authService from '@/services/authService.ts';
import { useAuth } from '@/context/AuthContext.tsx';
import FormError from '@/components/shared/FormError.tsx';

export default function DataExportSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await authService.exportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10);
      const handle = user?.email?.split('@')[0] ?? 'user';
      const a = document.createElement('a');
      a.href = url;
      a.download = `roleplaylab-export-${handle}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('profile.dataExport.error', 'No se pudo exportar los datos'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Download className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div>
            <h3 className="text-base font-semibold text-foreground">
              {t('profile.dataExport.title', 'Exportar mis datos')}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('profile.dataExport.description', 'Descarga una copia completa de tus datos personales (perfil, sesiones, puntuaciones y reportes) en formato JSON. Ejerce tu derecho de portabilidad (Art. 20 RGPD).')}
            </p>
            {error && (
              <div className="mt-3">
                <FormError message={error} onDismiss={() => setError(null)} />
              </div>
            )}
          </div>
        </div>
        <Button onClick={handleExport} disabled={loading} className="shrink-0">
          {loading ? t('profile.dataExport.loading', 'Preparando...') : t('profile.dataExport.cta', 'Descargar JSON')}
        </Button>
      </div>
    </div>
  );
}
